/** @format */

import template from './gb-widget.html';
import './gb-widget.scss';

export const GbWidgetComponent = {
    template,
    require: {
        gbApp: '^^',
        gbPresenter: '^^',
    },
    bindings: {
        widget: '=',
        excludeStyles: '<',
    },
    transclude: true,
    controller: class GbWidgetComponent {
        constructor($log, $ccToast, $scope) {
            'ngInject';

            this.$log = $log;
            this.$ccToast = $ccToast;
            this.$scope = $scope;

            this.allStyles = {};
            this.stylesToApply = {};

            this.startX = null;
            this.startY = null;

            this.requiresResave = false;
        }

        onDrag(ev) {
            this.startX = ev.x;
            this.startY = ev.y;
        }

        onDrop(ev) {
            if (this.positionChanged(ev)) {
                this.setWidgetValues(ev);
                this.saveWidget();
            }
        }

        positionChanged(ev) {
            return ev.x !== this.startX || ev.y !== this.startY;
        }

        setWidgetValues(ev) {
            let xVal = ev.xPercent;
            let yVal = ev.yPercent;
            xVal = Number(xVal.toFixed(3));
            yVal = Number(yVal.toFixed(3));

            this.widget.x = xVal;
            this.widget.y = yVal;
        }

        saveWidget() {
            this.gbPresenter.savingWidget = true;
            this.widget.projector = this.gbApp.getProjectorId();

            return this.widget
                .$save({
                    auction: this.gbApp.getAuctionUrl(),
                })
                .then((data) => {
                    this.$log.info('Saved Widget', data);

                    this.removeFromTempWidgets();

                    this.widget.constructor.cache.push(data);
                })
                .catch((err) => {
                    this.$ccToast.error(err);
                    throw new Error(err);
                })
                .finally(() => {
                    this.gbPresenter.savingWidget = false;
                });
        }

        // Widgets are added to this array before being saved
        removeFromTempWidgets() {
            let i = this.gbPresenter.tempWidgets.indexOf(this.widget);

            if (i > -1) {
                this.gbPresenter.tempWidgets.splice(i, 1);
            }
        }

        setStyles() {
            let styles = {};

            styles['left'] = typeof this.widget.x === 'number' ? `${this.widget.x}${this.widget.x_unit}` : undefined;
            styles['top'] = typeof this.widget.y === 'number' ? `${this.widget.y}${this.widget.y_unit}` : undefined;
            styles['width'] =
                typeof this.widget.width === 'number' ? `${this.widget.width}${this.widget.width_unit}` : undefined;
            styles['height'] =
                typeof this.widget.height === 'number' ? `${this.widget.height}${this.widget.height_unit}` : undefined;
            styles['transform'] = `rotateZ(${this.widget.rotate_z || 0}${this.widget.rotate_z_unit})`;

            styles['background-color'] = this.widget.background_color;
            styles['color'] = this.widget.color;
            styles['opacity'] = this.widget.opacity;

            styles['border-width'] =
                typeof this.widget.border_width === 'number'
                    ? `${this.widget.border_width}${this.widget.border_width_unit}`
                    : undefined;
            styles['border-style'] = this.widget.border_style;
            styles['border-color'] = this.widget.border_color;
            styles['border-radius'] =
                typeof this.widget.border_radius === 'number'
                    ? `${this.widget.border_radius}${this.widget.border_radius_unit}`
                    : undefined;

            styles['z-index'] = this.widget.z;
            styles['font-size'] = this.widget.font_size ? `${this.widget.font_size / 5}vh` : undefined;

            styles['font-family'] = this.widget.font_family || undefined;
            styles['font-weight'] = this.widget.font_weight || undefined;
            styles['padding'] =
                typeof this.widget.padding === 'number'
                    ? `${this.widget.padding}${this.widget.padding_unit}`
                    : undefined;

            styles['text-align'] = this.widget.text_align || undefined;

            this.allStyles = styles;
            this.stylesToApply = styles;

            if (Array.isArray(this.excludeStyles)) {
                this.stylesToApply = angular.copy(this.allStyles);

                for (let key of this.excludeStyles) {
                    delete this.stylesToApply[key];
                }
            }
        }

        setDefaults() {
            if (!this.widget.x_unit) {
                this.widget.x_unit = '%';
            }

            if (typeof this.widget.x !== 'number') {
                let val = parseInt(this.widget.x);
                if (isNaN(val)) {
                    this.widget.x = 0;
                } else {
                    this.widget.x = val;
                }
            }

            if (!this.widget.y_unit) {
                this.widget.y_unit = '%';
            }

            if (typeof this.widget.y !== 'number') {
                let val = parseInt(this.widget.y);
                if (isNaN(val)) {
                    this.widget.y = 0;
                } else {
                    this.widget.y = val;
                }
            }

            if (this.widget.z === undefined) {
                let widgets = this.gbPresenter.getLayerOrderedWidgets();
                this.widget.z = widgets.length + 1;
                this.gbPresenter.setWidgetLayers();
                this.requiresResave = true;
            }

            if (!this.widget.width_unit) {
                this.widget.width_unit = '%';
            }

            if (typeof this.widget.width !== 'number') {
                let val = parseInt(this.widget.width);
                if (isNaN(val)) {
                    this.widget.width = null;
                } else {
                    this.widget.width = val;
                }
            }

            if (!this.widget.height_unit) {
                this.widget.height_unit = '%';
            }

            if (typeof this.widget.height !== 'number') {
                let val = parseInt(this.widget.height);
                if (isNaN(val)) {
                    this.widget.height = null;
                } else {
                    this.widget.height = val;
                }
            }

            if (!this.widget.rotate_z_unit) {
                this.widget.rotate_z_unit = 'deg';
            }

            if (typeof this.widget.rotate_z !== 'number') {
                let val = parseInt(this.widget.rotate_z);
                if (isNaN(val)) {
                    this.widget.rotate_z = 0;
                } else {
                    this.widget.rotate_z = val;
                }
            }
        }

        selectWidget(ev) {
            this.gbPresenter.selectWidget(this.widget.uuid);

            if (ev) {
                ev.stopPropagation();
            }
        }

        isSelected() {
            return this.gbPresenter.widgetSelected(this.widget.uuid);
        }

        isNewWidget() {
            return !this.widget.uuid;
        }

        $onInit() {
            this.setDefaults();
            this.setStyles();

            this.$scope.$watch(
                () => this.widget,
                (newVal, oldVal) => {
                    if (newVal && newVal !== oldVal) {
                        this.setDefaults();
                        this.setStyles();
                    }
                },
                true
            );

            if (this.gbApp.isEditMode() && this.widget && this.isNewWidget()) {
                this.saveWidget().then(() => {
                    this.selectWidget();
                });
            }

            if (this.requiresResave) {
                this.gbPresenter.saveAllWidgets();
            }
        }
    },
};
